import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { ACORIS_PROFILES, ATTACHMENT_TYPES } from "utils/constants";
import { downloadUploadedDocument } from "utils/helpers/files";
import ActionButtons from "../Components/ActionButtons";
import Header from "../Components/Header";
import Layout from "../Components/Layout";
import { getAttestationHint } from "../utils/attestationHint";
import DocumentUpload from "./DocumentUpload";
import IdentityDocumentSides from "./IdentityDocumentSides";
import validation from "./validation";
import { useMemo } from "react";
import { normalizeAcorisProfile } from "../utils/insurancePlan";

const ELIGIBILITY_PROFILES = [
  ACORIS_PROFILES.TERRITORIAL_AGENT,
  ACORIS_PROFILES.TNS,
  ACORIS_PROFILES.CROSS_BORDER_WORKER,
];

function View({
  initialValues,
  onSubmit,
  downloadFile,
  onBack,
  supportingDocuments,
  isRia,
  paymentConditions = {},
  documentsLoading,
  onFileRemove: handleFileRemove,
  translate,
  project,
  isSigned = false,
}) {
  const isTax = supportingDocuments.includes(ATTACHMENT_TYPES.PAY_SLIP);
  const isProfessional = supportingDocuments.includes(
    ATTACHMENT_TYPES.PROFESSIONAL_PAYMENT_ACCOUNT,
  );
  const isEnrollmentCertificate = supportingDocuments.includes(
    ATTACHMENT_TYPES.PROFESSIONAL_PAYMENT_ACCOUNT,
  );
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);

  // Eligibility justificatif depends on the prospect profile (JSON parcours).
  const requiresEligibilityProof = useMemo(() => {
    const profileType = normalizeAcorisProfile(
      get(project, "contact.fields.profile") ||
        get(project, "fields.profileType"),
    );
    return ELIGIBILITY_PROFILES.includes(profileType);
  }, [project]);

  const {
    requiresSubscriberPaysButDifferentReceiver = false,
    requiresThirdPartyPayer = false,
    requiresBothThirdParty = false,
    requiresTwoSeparateRibs = false,
  } = paymentConditions;

  const attestationHint = getAttestationHint(
    get(project, "contact.socialRegime"),
    {
      general: translate?.DOCUMENTS?.SOCIAL_SECURITY_CERTIFICATE?.HINT_GENERAL,
      local: translate?.DOCUMENTS?.SOCIAL_SECURITY_CERTIFICATE?.HINT_LOCAL,
    },
  );

  const { isValid, submitForm, setFieldValue, values, ...formProps } =
    useFormik({
      initialValues: {
        cinRecto: initialValues?.cinRecto || [],
        cinVerso: initialValues?.cinVerso || [],
        paymentAccount: initialValues?.paymentAccount || [],
        refundAccount: initialValues?.refundAccount || [],
        socialSecurityCertificate:
          initialValues?.socialSecurityCertificate || [],
        payerCinRecto: initialValues?.payerCinRecto || [],
        payerCinVerso: initialValues?.payerCinVerso || [],
        receiverCinRecto: initialValues?.receiverCinRecto || [],
        receiverCinVerso: initialValues?.receiverCinVerso || [],
        proofOfAddress: initialValues?.proofOfAddress || [],
        proofOfEligibility: initialValues?.proofOfEligibility || [],
        contributionPaymentAgreement:
          initialValues?.contributionPaymentAgreement || [],
        socialSecurityCertificateForChildren:
          initialValues?.socialSecurityCertificateForChildren || false,
      },
      enableReinitialize: true,
      onSubmit: onSubmit,
      validationSchema: validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, {
          returnObjects: true,
        }),
        {
          isTax,
          isRia,
          isProfessional,
          isEnrollmentCertificate,
          requiresSubscriberPaysButDifferentReceiver,
          requiresThirdPartyPayer,
          requiresBothThirdParty,
          requiresTwoSeparateRibs,
          requiresEligibilityProof,
        },
      ),
    });

  const createFileRemoveHandler = (fieldName) => async (item) => {
    if (handleFileRemove) {
      await handleFileRemove(fieldName)(item);
      // Remove file from form values immediately
      const currentFiles = values[fieldName] || [];
      const updatedFiles = currentFiles.filter(
        (file) => (file?.uid || file?.id) !== (item?.uid || item?.id),
      );
      setFieldValue(fieldName, updatedFiles);
    }
  };

  const handleDownload = ({ uid, size, base64, contentType, name }) => {
    if (!size) return downloadFile(uid);
    downloadUploadedDocument({
      base64,
      contentType,
      name,
    });
  };

  return (
    <Layout>
      <Header
        title={
          translate?.HEADER?.TITLE ||
          "Dernière chose ! Nous avons besoin de vos pièces justificatives"
        }
        highlight={translate?.HEADER?.HIGHLIGHT || "pièces justificatives"}
        subtitle={
          translate?.HEADER?.SUBTITLE || "Pièces justificatives de l'adhérent"
        }
      />

      <div className="acoris-form">
        <div className="acoris-form__container">
          <div className="acoris-justification__documents">
            {/* Adherent identity document - always required */}
            <IdentityDocumentSides
              rectoField="cinRecto"
              versoField="cinVerso"
              values={values}
              setFieldValue={setFieldValue}
              createFileRemoveHandler={createFileRemoveHandler}
              handleDownload={handleDownload}
              errors={formProps.errors}
              translate={translate}
            />

            {/* Subscriber pays but doesn't receive - Requires: CIN receiver + RIB receiver */}
            {requiresSubscriberPaysButDifferentReceiver &&
              !requiresBothThirdParty && (
                <IdentityDocumentSides
                  rectoField="receiverCinRecto"
                  versoField="receiverCinVerso"
                  values={values}
                  setFieldValue={setFieldValue}
                  createFileRemoveHandler={createFileRemoveHandler}
                  handleDownload={handleDownload}
                  errors={formProps.errors}
                  translate={translate}
                  labelKeys={{
                    recto: "RECEIVER_CIN_RECTO",
                    verso: "RECEIVER_CIN_VERSO",
                  }}
                />
              )}

            {/* Third-party payer: Subscriber doesn't pay - Requires: CIN payer */}
            {requiresThirdPartyPayer && !requiresBothThirdParty && (
              <IdentityDocumentSides
                rectoField="payerCinRecto"
                versoField="payerCinVerso"
                values={values}
                setFieldValue={setFieldValue}
                createFileRemoveHandler={createFileRemoveHandler}
                handleDownload={handleDownload}
                errors={formProps.errors}
                translate={translate}
                labelKeys={{
                  recto: "PAYER_CIN_RECTO",
                  verso: "PAYER_CIN_VERSO",
                }}
              />
            )}

            {/* Subscriber doesn't pay AND doesn't receive - Requires: payer + receiver CINs */}
            {requiresBothThirdParty && (
              <>
                <IdentityDocumentSides
                  rectoField="payerCinRecto"
                  versoField="payerCinVerso"
                  values={values}
                  setFieldValue={setFieldValue}
                  createFileRemoveHandler={createFileRemoveHandler}
                  handleDownload={handleDownload}
                  errors={formProps.errors}
                  translate={translate}
                  labelKeys={{
                    recto: "PAYER_CIN_RECTO",
                    verso: "PAYER_CIN_VERSO",
                  }}
                />
                <IdentityDocumentSides
                  rectoField="receiverCinRecto"
                  versoField="receiverCinVerso"
                  values={values}
                  setFieldValue={setFieldValue}
                  createFileRemoveHandler={createFileRemoveHandler}
                  handleDownload={handleDownload}
                  errors={formProps.errors}
                  translate={translate}
                  labelKeys={{
                    recto: "RECEIVER_CIN_RECTO",
                    verso: "RECEIVER_CIN_VERSO",
                  }}
                />
              </>
            )}

            {/* CA-02: RIB - At least 1 RIB always required */}
            {requiresTwoSeparateRibs ? (
              <>
                <DocumentUpload
                  label={
                    translate?.DOCUMENTS?.PAYER_RIB?.LABEL ||
                    "Relevé d'Identité Bancaire (RIB)"
                  }
                  items={values.paymentAccount || []}
                  onDownload={handleDownload}
                  onUpload={(item) => {
                    setFieldValue("paymentAccount", item);
                  }}
                  onFileRemove={createFileRemoveHandler("paymentAccount")}
                  error={get(formProps.errors, "paymentAccount")}
                  required
                />
                <DocumentUpload
                  label={
                    translate?.DOCUMENTS?.RECEIVER_RIB?.LABEL ||
                    "RIB Prestation"
                  }
                  items={values.refundAccount || []}
                  onDownload={handleDownload}
                  onUpload={(item) => {
                    setFieldValue("refundAccount", item);
                  }}
                  onFileRemove={createFileRemoveHandler("refundAccount")}
                  error={get(formProps.errors, "refundAccount")}
                  required
                />
              </>
            ) : (
              <DocumentUpload
                label={
                  translate?.DOCUMENTS?.PAYMENT_ACCOUNT?.LABEL ||
                  "Relevé d'Identité Bancaire (RIB)"
                }
                items={values.paymentAccount || []}
                onDownload={handleDownload}
                onUpload={(item) => {
                  setFieldValue("paymentAccount", item);
                }}
                onFileRemove={createFileRemoveHandler("paymentAccount")}
                error={get(formProps.errors, "paymentAccount")}
                required
              />
            )}

            {/* CA-01: Attestation d'assurance maladie */}
            <DocumentUpload
              label={
                translate?.DOCUMENTS?.SOCIAL_SECURITY_CERTIFICATE?.LABEL ||
                "Attestation de droits de l'Assurance Maladie"
              }
              hint={attestationHint}
              items={values.socialSecurityCertificate || []}
              onDownload={handleDownload}
              onUpload={(item) => {
                setFieldValue("socialSecurityCertificate", item);
              }}
              onFileRemove={createFileRemoveHandler(
                "socialSecurityCertificate",
              )}
              error={get(formProps.errors, "socialSecurityCertificate")}
              required
            />

            {/* CA-04: Proof of eligibility - optional, never blocks */}
            {requiresEligibilityProof && (
              <DocumentUpload
                label={
                  translate?.DOCUMENTS?.PROOF_OF_ELIGIBILITY?.LABEL ||
                  "Justificatif d'éligibilité"
                }
                items={values.proofOfEligibility || []}
                onDownload={handleDownload}
                onUpload={(item) => {
                  setFieldValue("proofOfEligibility", item);
                }}
                onFileRemove={createFileRemoveHandler("proofOfEligibility")}
                error={get(formProps.errors, "proofOfEligibility")}
                required
              />
            )}

            {/* CA-03: Proof of address - optional, never blocks */}
            <DocumentUpload
              label={
                translate?.DOCUMENTS?.PROOF_OF_ADDRESS?.LABEL ||
                "Justificatif de domicile de moins de 3 mois"
              }
              items={values.proofOfAddress || []}
              onDownload={handleDownload}
              onUpload={(item) => {
                setFieldValue("proofOfAddress", item);
              }}
              onFileRemove={createFileRemoveHandler("proofOfAddress")}
              error={get(formProps.errors, "proofOfAddress")}
            />

            {/* Tiers payeur: attestation sur l'honneur (JSON parcours). */}
            {(requiresThirdPartyPayer || requiresBothThirdParty) && (
              <DocumentUpload
                label={
                  translate?.DOCUMENTS?.CONTRIBUTION_PAYMENT_AGREEMENT?.LABEL ||
                  "Attestation sur l'honneur du tiers payeur"
                }
                items={values.contributionPaymentAgreement || []}
                onDownload={handleDownload}
                onUpload={(item) => {
                  setFieldValue("contributionPaymentAgreement", item);
                }}
                onFileRemove={createFileRemoveHandler(
                  "contributionPaymentAgreement",
                )}
                error={get(formProps.errors, "contributionPaymentAgreement")}
                required
              />
            )}

            <div className="acoris-form__info">
              <p>
                {translate?.FILE_INFO?.SUPPORTED_FORMATS ||
                  "Formats de fichiers pris en charge : JPEG, PNG ou PDF"}
              </p>
              <p>
                {translate?.FILE_INFO?.MAX_SIZE || "Taille maximale : 5 Mo"}
              </p>
            </div>
          </div>
          <ActionButtons
            onNext={submitForm}
            buttonText={translate?.BUTTON?.CONTINUE || "Continuer"}
            onBack={onBack}
            nextProps={{
              disabled: !isValid || !isEmpty(formProps.errors),
              loading: documentsLoading,
            }}
            backProps={{
              disabled: isSigned,
            }}
          />
        </div>
      </div>
    </Layout>
  );
}

export default View;
